import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  useToast,
  VStack,
  BoxProps,
  Text,
  chakra,
  HStack,
  IconButton,
  Button,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import moo from 'moo';
import classNames from 'classnames';
import { RefreshIcon } from '../../../../../components/icons';

type Word = {
  index?: number;
  match?: string;
};

interface MarkTheWordsProps extends BoxProps {
  text: string;
  words: Word[];
  debug?: boolean;
  isViewOnly?: boolean;
  showCorrect?: boolean;
  submit?: (correct: number, total: number, state: any) => void;
  reset?: () => void;
  initialState?: { state: boolean[]; selected: boolean[] };
}

function escapeRegExp(text: string) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const MarkTheWords: React.FC<MarkTheWordsProps> = ({
  text,
  words,
  debug,
  submit,
  reset,
  initialState,
  isViewOnly,
  showCorrect,
  ...props
}) => {
  const { t } = useTranslation();
  const tokens = useMemo(() => {
    const parts = [/(?:[a-zA-Z'"`-]+)/];

    words.forEach((w) => {
      if (w.match != null && w.match.includes(' '))
        parts.push(new RegExp(escapeRegExp(w.match)));
    });

    const regex = parts.reduce((regex, part) => {
      return new RegExp('(?:' + part.source + ')|' + regex.source);
    });

    const lexer = moo.compile({
      word: regex,
      punctuation: { match: /[\s.,!?;]+/, lineBreaks: true },
    });

    return Array.from(lexer.reset(text));
  }, [words]);
  const [state, setState] = useState<boolean[]>(words.map((s) => false));
  const [showResults, setShowResults] = useState<boolean>(false);
  const [shownCorrect, setShownCorrect] = useState<number>(0);
  const [selected, setSelected] = useState<boolean[]>(tokens.map((s) => false));

  const positionMap = useMemo(() => {
    let positionMap = new Map<number, number>();
    let wordCount = 0;
    tokens.forEach((token, index) => {
      if (token.type === 'word') {
        positionMap.set(wordCount, index);
        wordCount++;
      }
    });

    return positionMap;
  }, [words]);

  const setInitialState = () => {
    setState(words.map((s) => false));
    setSelected(tokens.map((s) => false));
  };

  useEffect(() => {
    if (initialState) {
      setState(initialState.state);
      setSelected(initialState.selected);
      setShowResults(true);
      let correct = 0;

      initialState.state.forEach((v) => {
        if (v) correct += 1;
      });

      setShownCorrect(correct);
    } else setInitialState();
  }, [words, initialState]);

  const _submit = () => {
    setShowResults(true);
    let correct = 0;

    state.forEach((v) => {
      if (v) correct += 1;
    });

    setShownCorrect(correct);
    if (submit != null) {
      submit(correct, words.length, { state, selected });
    }
  };

  const _reset = () => {
    setInitialState();
    setShowResults(false);
    setShownCorrect(0);
    if (reset != null) reset();
  };

  const ret = (
    <VStack>
      <Box position="relative" {...props}>
        <Text zIndex={0}>
          {tokens.map((token, index) => {
            if (token.type === 'word') {
              return (
                <chakra.span
                  position="relative"
                  className={classNames([
                    { selected: selected[index] === true },
                    {
                      correct:
                        showCorrect &&
                        words.find(
                          (w) => positionMap.get(w.index as number) === index,
                        ),
                    },
                  ])}
                  sx={{
                    '&:hover, &.selected': {
                      color: 'white',
                    },
                    '&.selected div': {
                      background: 'rgba(16,46,142,1)',
                    },
                    '&:hover div, &.selected:hover div': {
                      background: 'rgba(16,46,142,0.35)',
                    },
                    '&:hover:active div, &.selected:hover:active div': {
                      background: 'rgba(16,46,142,0.75)',
                    },
                    '&.correct div': {
                      background: '#38a169',
                    },
                  }}
                  onClick={(e) => {
                    const newSelected = [...selected];
                    const newState = [...state];

                    newSelected[index] = !newSelected[index];

                    words.forEach((word, i) => {
                      if (word.index != null) {
                        if (positionMap.has(word.index)) {
                          const tokenIndex = positionMap.get(word.index)!;
                          newState[i] = newSelected[tokenIndex] === true;
                        }
                      } else if (word.match) {
                        const totalTokenCount = tokens.filter(
                          (t) => t.text === word.match,
                        ).length;
                        const selectedTokenCount = tokens.filter(
                          (t, index) =>
                            t.text === word.match && newSelected[index],
                        ).length;
                        newState[i] = selectedTokenCount === totalTokenCount;
                      }
                    });

                    setSelected(newSelected);
                    setState(newState);
                  }}
                >
                  <chakra.span position="relative" zIndex={1}>
                    {token.text}
                  </chakra.span>
                  <chakra.div
                    zIndex={0}
                    background="transparent"
                    position="absolute"
                    borderRadius="8px"
                    top="-2px"
                    bottom="-2px"
                    left="-2px"
                    right="-2px"
                    transition="all 150ms ease-in-out"
                  />
                </chakra.span>
              );
            }

            return (
              <chakra.span position="relative" zIndex={1}>
                {token.text}
              </chakra.span>
            );
          })}
        </Text>
      </Box>
      <HStack
        minW={showResults ? '300px' : undefined}
        spacing={0}
        gap={2}
        justify="space-between"
      >
        {showResults && (
          <>
            {showResults && (
              <IconButton
                size="sm"
                colorScheme="stemiBlue"
                variant="ghost"
                aria-label="reset"
                icon={<RefreshIcon boxSize="24px" />}
                onClick={_reset}
              >
                {t('general.reset')}
              </IconButton>
            )}
            <HStack>
              <span>
                {shownCorrect} / {words.length}
              </span>
            </HStack>
          </>
        )}
        <HStack>
          {!isViewOnly && (
            <Button colorScheme="blue" onClick={_submit}>
              {t('general.submit')}
            </Button>
          )}
        </HStack>
      </HStack>
    </VStack>
  );

  if (debug) {
    return (
      <VStack w="full">
        {ret}
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return <>{ret}</>;
};

export default MarkTheWords;
